import { Injectable } from '@angular/core';
import { FireflyService } from './firefly.service';
import { ToastService } from './toast.service';
import {
  StyleCategory,
  StyleLabelMap,
  StyleImage,
  FireflyGenerationOptions,
  StyleIdMapping,
  FireflyGenerationOptionsIm5
} from '../models/firefly-common.model';

// サイズプリセットの型定義
export interface SizePreset {
  width: number;
  height: number;
  label: string;
  model?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FireflyConfigService {
  // スタイルIDの互換性マッピング
  private readonly styleIdMapping: StyleIdMapping = {
    'line_drawing': 'line-drawing',
    'line-art': 'line-drawing',
    'pop_art': 'pop-art',
    'pop-art': 'pop_art'
  };

  // スタイルカテゴリ定義
  readonly styleCategories: StyleCategory[] = [
    { id: 'popular', label: 'styleCat.popular', styles: ['photographic', 'flat', 'line-drawing', 'sketch', 'pop-art', 'anime'] },
    {
      id: 'movements', label: 'styleCat.movements', styles: [
        'art_deco', 'art_nouveau', 'baroque', 'bauhaus', 'constructivism', 'cubism', 'cyberpunk',
        'fantasy', 'fauvism', 'film_noir', 'glitch_art', 'impressionism', 'industrial', 'maximalism',
        'minimalism', 'modern_art', 'modernism', 'neo-expressionism', 'pointillism', 'psychedelic',
        'science_fiction', 'steampunk', 'surrealism', 'synthetism', 'synthwave', 'vaporwave'
      ]
    },
    {
      id: 'themes', label: 'styleCat.themes', styles: [
        '3d', 'anime', 'cartoon', 'cinematic', 'comic_book', 'concept_art', 'cyber_matrix',
        'digital_art', 'flat_design', 'geometric', 'glassmorphism', 'glitch_graphic', 'graffiti',
        'graphic_1', 'hyper_realistic', 'interior_design', 'line_gradient', 'low_poly',
        'newspaper_collage', 'optical_illusion', 'pattern_pixel', 'pixel_art', 'pop_art',
        'product_photo', 'psychedelic_background', 'psychedelic_wonderland', 'scandinavian',
        'splash_images', 'stamp', 'trompe_loeil', 'vector_look', 'wireframe'
      ]
    },
    {
      id: 'techniques', label: 'styleCat.techniques', styles: [
        'acrylic_paint', 'bold_lines', 'chiaroscuro', 'color_shift_art', 'daguerreotype',
        'digital_fractal', 'doodle_drawing', 'double_exposure_portrait', 'fresco', 'geometric_pen',
        'halftone', 'ink', 'light_painting', 'line_drawing', 'linocut', 'oil_paint',
        'paint_splattering', 'painting', 'palette_knife_paint', 'photo_manipulation',
        'scribble_texture', 'sketch', 'splattering', 'stippling_drawing', 'watercolor'
      ]
    },
    {
      id: 'effects', label: 'styleCat.effects', styles: [
        'antique_photo', 'bioluminescent', 'bokeh', 'color_explosion', 'dark', 'faded_image',
        'fisheye', 'gomori_photography', 'grainy_film', 'iridescent', 'isometric', 'misty',
        'neon', 'otherworldly_depiction', 'ultraviolet', 'underwater'
      ]
    },
    {
      id: 'materials', label: 'styleCat.materials', styles: [
        '3d_patterns', 'charcoal', 'claymation', 'fabric', 'fur', 'guilloche_patterns',
        'layered_paper', 'marble_sculpture', 'made_of_metal', 'origami', 'paper_mache',
        'polka-dot_pattern', 'strange_patterns', 'wood_carving', 'yarn'
      ]
    },
    {
      id: 'concepts', label: 'styleCat.concepts', styles: [
        'beautiful', 'bohemian', 'chaotic', 'dais', 'divine', 'eclectic', 'futuristic',
        'kitschy', 'nostalgic', 'simple'
      ]
    },
  ];

  // スタイル名のマッピング
  readonly styleLabels: StyleLabelMap = {
    // 人気/基本スタイル
    'photographic': '写真スタイル',
    'flat': 'フラットデザイン',
    'line_drawing': '線画',
    'line-drawing': '線画',
    'line-art': 'ラインアート',
    'pop_art': 'ポップアート',
    'pop-art': 'ポップアート',
    'sketch': 'スケッチ',
    'anime': 'アニメ',

    // 流行
    'art_deco': 'アールデコ',
    'art_nouveau': 'アールヌーボー',
    'baroque': 'バロック',
    'bauhaus': 'バウハウス',
    'constructivism': '構成主義',
    'cubism': 'キュービズム',
    'cyberpunk': 'サイバーパンク',
    'fantasy': 'ファンタジー',
    'fauvism': 'フォービズム',
    'film_noir': 'フィルムノワール',
    'glitch_art': 'グリッチアート',
    'impressionism': '印象派',
    'industrial': 'インダストリアル',
    'maximalism': 'マキシマリズム',
    'minimalism': 'ミニマリズム',
    'modern_art': 'モダンアート',
    'modernism': 'モダニズム',
    'neo-expressionism': '新表現主義',
    'pointillism': '点描画法',
    'psychedelic': 'サイケデリック',
    'science_fiction': 'SF',
    'steampunk': 'スチームパンク',
    'surrealism': 'シュールレアリスム',
    'synthetism': '総合主義',
    'synthwave': 'シンセウェーブ',
    'vaporwave': 'ヴェイパーウェイヴ',

    // テーマ
    '3d': '3D',
    'cartoon': 'カートゥーン',
    'cinematic': '映画風',
    'comic_book': 'コミックブック',
    'concept_art': 'コンセプトアート',
    'cyber_matrix': 'サイバーマトリックス',
    'digital_art': 'デジタルアート',
    'flat_design': 'フラットデザイン',
    'geometric': '幾何学的',
    'glassmorphism': 'グラスモーフィズム',
    'glitch_graphic': 'グリッチグラフィック',
    'graffiti': 'グラフィティ',
    'graphic_1': 'グラフィック',
    'hyper_realistic': '超現実的',
    'interior_design': 'インテリアデザイン',
    'line_gradient': '線形グラデーション',
    'low_poly': 'ローポリ',
    'newspaper_collage': '新聞紙のコラージュ',
    'optical_illusion': '錯視',
    'pattern_pixel': 'パターンピクセル',
    'pixel_art': 'ドット絵',
    'product_photo': '製品写真',
    'psychedelic_background': 'サイケデリックな背景',
    'psychedelic_wonderland': 'サイケデリックワンダーランド',
    'scandinavian': 'スカンジナビア風',
    'splash_images': 'スプラッシュ画像',
    'stamp': 'スタンプ',
    'trompe_loeil': 'トロンプルイユ',
    'vector_look': 'ベクター風',
    'wireframe': 'ワイヤフレーム',

    // テクニック
    'acrylic_paint': 'アクリル絵の具',
    'bold_lines': '太い線',
    'chiaroscuro': 'キアロスクーロ',
    'color_shift_art': 'カラーシフトアート',
    'daguerreotype': '銀板写真',
    'digital_fractal': 'デジタルフラクタル',
    'doodle_drawing': '落書き',
    'double_exposure_portrait': '二重露光ポートレート',
    'fresco': 'フレスコ',
    'geometric_pen': '幾何学的なペン',
    'halftone': 'ハーフトーン',
    'ink': 'インク',
    'light_painting': 'ライトペインティング',
    'linocut': 'リノカット',
    'oil_paint': '油絵',
    'paint_splattering': 'ペンキの飛び散り',
    'painting': 'ペイント',
    'palette_knife_paint': 'パレットナイフ',
    'photo_manipulation': '写真加工',
    'scribble_texture': '手書きテクスチャ',
    'splattering': 'スパッタリング',
    'stippling_drawing': '点刻',
    'watercolor': '水彩画',

    // 効果
    'antique_photo': 'アンティーク写真',
    'bioluminescent': '生物発光',
    'bokeh': 'ボケ効果',
    'color_explosion': '色の爆発',
    'dark': '暗い',
    'faded_image': '色あせた画像',
    'fisheye': '魚眼',
    'gomori_photography': 'ゴモリ写真',
    'grainy_film': '粒子が粗いフィルム',
    'iridescent': '虹色',
    'isometric': 'アイソメトリック',
    'misty': 'もや',
    'neon': 'ネオン',
    'otherworldly_depiction': '空想的な描写',
    'ultraviolet': '紫外線',
    'underwater': '水中',

    // マテリアル
    '3d_patterns': '3D パターン',
    'charcoal': '木炭画',
    'claymation': 'クレイメーション',
    'fabric': 'ファブリック',
    'fur': '毛皮',
    'guilloche_patterns': 'ギローシェ模様',
    'layered_paper': '彫紙',
    'marble_sculpture': '大理石',
    'made_of_metal': '金属製',
    'origami': '折り紙',
    'paper_mache': '張り子',
    'polka-dot_pattern': '水玉模様',
    'strange_patterns': '奇妙なパターン',
    'wood_carving': '木彫り',
    'yarn': '糸',

    // コンセプト
    'beautiful': '美しい',
    'bohemian': 'ボヘミアン',
    'chaotic': '雑然さ',
    'dais': '台座',
    'divine': '神聖',
    'eclectic': '折衷主義',
    'futuristic': '未来派',
    'kitschy': 'キッチュ',
    'nostalgic': '懐かしさ',
    'simple': 'シンプル',
  };

  // サイズプリセット定義
  readonly sizePresets: SizePreset[] = [
    { width: 1024, height: 1024, label: '正方形 (1024 x 1024)', model: 'image3' },
    { width: 2048, height: 2048, label: '正方形 大 (2048 x 2048)', model: 'all' },
    { width: 2304, height: 1792, label: '横長 4:3 (2304 x 1792)', model: 'all' },
    { width: 1344, height: 768, label: '横長 7:4 (1344 x 768)', model: 'image3' },
    { width: 1344, height: 756, label: '横長 7:4 (1344 x 756)', model: 'image3' },
    { width: 1152, height: 896, label: '横長 9:7 (1152 x 896)', model: 'image3' },
    { width: 2688, height: 1536, label: 'ワイド 16:9 (2688 x 1536)', model: 'all' },
    { width: 2688, height: 1512, label: 'ワイド 16:9 (2688 x 1512)', model: 'image3' },
    { width: 1792, height: 2304, label: '縦長 3:4 (1792 x 2304)', model: 'all' },
    { width: 1440, height: 2560, label: '縦長 9:16 大 (1440 x 2560)', model: 'image4' },
    { width: 896, height: 1152, label: '縦長 7:9 (896 x 1152)', model: 'image3' }
  ];

  // デフォルト値
  readonly DEFAULT_STYLE_STRENGTH: number = 68; // 両方の中間値（66と70の中間）
  readonly DEFAULT_STRUCTURE_STRENGTH: number = 60;
  readonly DEFAULT_VISUAL_INTENSITY: number = 10;
  readonly DEFAULT_STYLE_PRESETS: string[] = ['line-drawing', 'sketch'];

  constructor(
    private fireflyService: FireflyService,
    private toastService: ToastService
  ) { }

  /**
   * 標準化されたスタイルIDを取得
   */
  getNormalizedStyleId(styleId: string): string {
    return this.styleIdMapping[styleId] || styleId;
  }

  /**
   * スタイルラベルを取得（互換性対応）
   */
  getStyleLabel(styleId: string): string {
    const normalizedId = this.getNormalizedStyleId(styleId);
    return this.styleLabels[normalizedId] || normalizedId;
  }

  /**
   * フィルタリングされたスタイルを取得
   */
  getFilteredStyles(searchTerm: string, activeCategory: string, isSearchActive: boolean): string[] {
    if (isSearchActive) {
      // 検索フィルタリング
      const searchLower = searchTerm.toLowerCase();
      const results: string[] = [];

      for (const [styleId, label] of Object.entries(this.styleLabels)) {
        if (label.toLowerCase().includes(searchLower) ||
          styleId.toLowerCase().includes(searchLower)) {
          results.push(styleId);
        }
      }

      return results;
    } else {
      // カテゴリ表示
      const category = this.styleCategories.find(c => c.id === activeCategory);
      return category ? category.styles : [];
    }
  }

  
  buildGenerationRequestIm5(options: FireflyGenerationOptionsIm5): any {
    const requestBody: any = {
      prompt: options.prompt,
      numVariations: options.numVariations || 1,
      aspectRatio: options.aspectRatio || 'auto',
      modelId: options.modelId || 'firefly_image',
      modelVersion: options.modelVersion || 'image5',
      resolutionLevel: options.resolutionLevel || '2.4MP',
      localeCode: options.promptBiasingLocaleCode || 'ja-JP',
      prompt_reasoner: options.promptReasoner || 'speed'
    };

    // Note there is a chance that this will change
    if(options.customModelId){
      requestBody.customModelId = options.customModelId;
    }

    // In the case of a reference image being set, the aspect ratio needs to be set to auto
    if (options.structureImageId) {
      requestBody.referenceBlobs = [{ source: { uploadId: options.structureImageId } }];
      requestBody.aspectRatio = 'auto';
    }

    if (options.seed !== undefined) {
      if (typeof options.seed === 'number' || (typeof options.seed === 'string' && options.seed.trim() !== '')) {
        const seedBase = Number(options.seed);
        if (!isNaN(seedBase)) {
          requestBody.seeds = Array.from(
            { length: options.numVariations || 1 },
            (_, i) => seedBase + i
          );
        }
      } else {
        const seedBase = Math.floor(100000000 + Math.random() * 900000000);
        requestBody.seeds = Array.from(
          { length: options.numVariations || 1 },
          (_, i) => seedBase + i
        );
      }
    }

    return requestBody;
  }
  
  /**
   * カスタムモデル生成リクエストを構築
   * @param options
   * @returns 
   */
  buildCustomModelGenerationRequest(options: FireflyGenerationOptions): any {
    const requestBody : any = {
      numVariations: options.numVariations || 1,
      prompt: options.prompt,
      promptBiasingLocaleCode: options.promptBiasingLocaleCode || 'ja-JP',
      size: {
        width: options.width || 1024,
        height: options.height || 1024
      },
      customModelId: options.customModelId,
      modelVersion: options.modelVersion
    }

    // Structure reference image (uploaded to Firefly)
    if (options.structureImageId) {
      requestBody.structure = {
        imageReference: { source: { uploadId: options.structureImageId } },
        strength: options.structureStrength || 95
      };
    }

    if (options.seed !== undefined) {
      if (typeof options.seed === 'number' || (typeof options.seed === 'string' && options.seed.trim() !== '')) {
        const seedBase = Number(options.seed);
        if (!isNaN(seedBase)) {
          requestBody.seeds = Array.from(
            { length: options.numVariations || 1 },
            (_, i) => seedBase + i
          );
        }
      } else {
        // ランダムシード
        const seedBase = Math.floor(100000000 + Math.random() * 900000000);
        requestBody.seeds = Array.from(
          { length: options.numVariations || 1 },
          (_, i) => seedBase + i
        );
      }
    }
    
    return requestBody;
  }

  /**
   * 画像生成リクエストを構築
   */
  buildGenerationRequest(options: FireflyGenerationOptions): any {
    if (options.customModelId) {
      return this.buildCustomModelGenerationRequest(options);
    }

    const requestBody: any = {
      contentClass: options.contentClass || 'photo',
      numVariations: options.numVariations || 1,
      prompt: options.prompt,
      promptBiasingLocaleCode: options.promptBiasingLocaleCode || 'ja-JP',
      size: {
        width: options.width || 1024,
        height: options.height || 1024
      },
      style: {
        presets: options.stylePresets || [],
        strength: options.styleStrength || this.DEFAULT_STYLE_STRENGTH
      },
      visualIntensity: options.visualIntensity || this.DEFAULT_VISUAL_INTENSITY
    };

    // 条件付きプロパティ
    if (options.modelVersion) {
      requestBody.modelVersion = options.modelVersion;
    }

    if (options.customModelId) {
      requestBody.customModelId = options.customModelId;
    }

    if (options.structureImageId) {
      requestBody.structure = {
        imageReference: { source: { uploadId: options.structureImageId } },
        strength: options.structureStrength || this.DEFAULT_STRUCTURE_STRENGTH
      };
    }

    if (options.styleImageId) {
      if (!requestBody.style) requestBody.style = {};
      requestBody.style.imageReference = {
        source: { uploadId: options.styleImageId }
      };
    }

    if (options.seed !== undefined) {
      if (typeof options.seed === 'number' || (typeof options.seed === 'string' && options.seed.trim() !== '')) {
        const seedBase = Number(options.seed);
        if (!isNaN(seedBase)) {
          requestBody.seeds = Array.from(
            { length: options.numVariations || 1 },
            (_, i) => seedBase + i
          );
        }
      } else {
        // ランダムシード
        const seedBase = Math.floor(100000000 + Math.random() * 900000000);
        requestBody.seeds = Array.from(
          { length: options.numVariations || 1 },
          (_, i) => seedBase + i
        );
      }
    }

    return requestBody;
  }

  /**
   * モデルバージョンに基づいて利用可能なサイズプリセットを取得
   */
  getAvailableSizePresets(modelVersion: string | undefined): SizePreset[] {
    const modelType = modelVersion?.startsWith('image4') ? 'image4' : 'image3';

    return this.sizePresets.filter(preset =>
      !preset.model || // モデル指定がないサイズはすべてのモデルで利用可能
      preset.model === 'all' ||
      preset.model === modelType
    );
  }

  /**
   * ファイルサイズのフォーマット
   */
  formatFileSize(bytes: number | undefined | null): string {
    if (bytes === undefined || bytes === null) {
      return '0 B';
    }

    if (bytes < 1024) {
      return bytes + ' B';
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + ' KB';
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    }
  }
}
